/**
 * P2PResolveSidebar — uses the same UI pattern as the M2C ResolveSidebar.
 * State-machine driven, always-mounted slide-in/out panel.
 * Props: lambdaUrl, rowData, isOpen, onClose, onStatusChange,
 *        phase, setPhase, agent1, setAgent1, agent2, setAgent2, activeCaseId
 */
import { useState, useRef, useEffect } from "react";
import { triggerAndPoll } from "../pages/p2pexceptions";
import EditableFieldsForm from "./EditableFieldsForm";
import P2PTrailPanel from "./P2PTrailPanel";

// ─── P2P Endpoints ────────────────────────────────────────────────────────────
const P2P_URL = "/api/p2p/exceptions";

// ─── Sidebar states ───────────────────────────────────────────────────────────
export const S = {
  IDLE:             "idle",
  AGENT1_LOADING:   "agent1_loading",
  AGENT1_DONE:      "agent1_done",
  AGENT1_ERROR:     "agent1_error",
  AGENT2_LOADING:   "agent2_loading",
  AGENT2_NO_REPLY:  "agent2_no_reply",
  AGENT2_PREVIEW:   "agent2_preview",
  AGENT2_ERROR:     "agent2_error",
  AGENT2_EDITING:   "agent2_editing",
  AGENT2_APPROVING: "agent2_approving",
  AGENT2_RESOLVED:  "agent2_resolved",
  AGENT2_REJECTED:  "agent2_rejected",
};

// ─── Fetch single row from P2P API ────────────────────────────────────────────
async function fetchCaseFromDB(exceptionId) {
  const res = await fetch(`${P2P_URL}/${exceptionId}`);
  if (!res.ok) throw new Error(`P2P fetch failed: ${res.status}`);
  const row = await res.json();
  if (!row) throw new Error(`Exception ${exceptionId} not found after agent run`);
  
  // Fetch agent actions separately
  try {
    const actionsRes = await fetch(`${P2P_URL}/${exceptionId}/actions`);
    if (actionsRes.ok) {
      const actionsData = await actionsRes.json();
      console.log('Actions fetched:', actionsData);
      if (actionsData.actions && actionsData.actions.length > 0) {
        // Map actions to timeline format (sorted by created_at desc, so reverse for chronological order)
        row.agent_actions = actionsData.actions
          .reverse()
          .map(a => ({
            description: a.action_tldr,
            action_type: a.action_type,
            outcome: a.outcome,
            status: a.outcome === 'PENDING' ? 'pending' : 'completed',
            created_at: a.created_at
          }));
      }
    }
  } catch (e) {
    console.warn('Could not fetch actions:', e);
  }
  
  // Fetch email communications separately
  try {
    const commRes = await fetch(`${P2P_URL}/${exceptionId}/communications`);
    if (commRes.ok) {
      const commData = await commRes.json();
      console.log('Communications fetched:', commData);
      if (commData.communications && commData.communications.length > 0) {
        // Map structured data from full_body JSON to email_content format
        row.email_content = commData.communications.map(c => {
          const body = c.full_body || {};
          console.log('Processing communication:', { recipient: c.recipient, subject: c.subject, body });
          return {
            to: c.recipient,
            subject: c.subject,
            body_preview: c.message_preview,
            sent_at: c.sent_at,
            // Include structured fields from full_body for display
            exception_code: body.exception_code,
            exception_name: body.exception_name,
            document_number: body.document_number,
            vendor_name: body.vendor_name,
            amount: body.amount,
            currency: body.currency,
            variance_amount: body.variance_amount,
            variance_pct: body.variance_pct,
            action: body.action,
            action_description: body.action_description,
            outcome: body.outcome,
            resolution_type: body.resolution_type,
            // HITL editing flags
            reconciliation_passed: body.reconciliation_passed,
            sop_satisfied: body.sop_satisfied,
            doc_type: body.doc_type,
          };
        });
        console.log('Mapped email_content:', row.email_content);
      }
    }
  } catch (e) {
    console.warn('Could not fetch communications:', e);
  }
  
  return row;
}

// ─── Map DB row → Agent 1 UI state ───────────────────────────────────────────
export function mapDbToAgent1State(row) {
  // Build base actions from agent_actions or action_performed
  const baseActions = row.agent_actions || row.action_performed?.action_details || [];
  
  // If email notification exists, append it as final action in timeline
  let actionsWithEmail = baseActions;
  if (row.email_content?.[0]) {
    const email = row.email_content[0];
    // Guard against fabricating "email sent" when no email was actually sent (e.g., internal notes)
    const label = email.email_sent === false ? 'Internal note' : (email.action || 'Email sent');
    const emailAction = {
      description: `${label}: ${email.body_preview || email.subject}`,
      action_type: 'notification',
      outcome: email.outcome || 'SENT',
      status: 'completed',
      created_at: email.sent_at
    };
    // Append email as last step in timeline
    actionsWithEmail = Array.isArray(baseActions) 
      ? [...baseActions, emailAction] 
      : [emailAction];
  }
  
  return {
    exception_information: {
      exception_id:   row.exception_id,
      invoice_number: row.document_number,
      exception_type: row.exception_name || null, // Allow null - agent will update
      customer_name:  row.vendor_name || null,
      variance_amount: row.amount ? `USD ${row.amount}` : null,
      status:         row.status,
      sop_reference:  row.sop_reference || null,
      overdue:        row.overdue || false,
    },
    actions_performed: actionsWithEmail,
    email_notification: row.email_content?.[0]
      ? {
          sent_to:      row.email_content[0].to,
          subject:      row.email_content[0].subject,
          body_preview: row.email_content[0].body_preview || null,
          sent_at:      row.email_content[0].sent_at,
          action:       row.email_content[0].action || null,
          outcome:      row.email_content[0].outcome || null,
          action_description: row.email_content[0].action_description || null,
          reconciliation_passed: row.email_content[0].reconciliation_passed || null,
          sop_satisfied: row.email_content[0].sop_satisfied || null,
          doc_type: row.email_content[0].doc_type || null,
        }
      : null,
    next_steps_and_deadlines: row.next_steps_and_deadlines || null,
  };
}

// ─── Map DB row → Agent 2 UI state ───────────────────────────────────────────
export function mapDbToAgent2State(row) {
  // Build resolution_outcome from latest email communication
  const latestEmail = row.email_content?.[0] || {};
  const resolutionOutcome = latestEmail.outcome ? {
    status: latestEmail.outcome,
    reason: latestEmail.body_preview || null,
  } : null;

  return {
    mode:               row.agent2_mode || null,
    resolution_outcome: resolutionOutcome,
    reply_analysis:     latestEmail.reconciliation_passed !== undefined ? {
      reconciliation_passed: latestEmail.reconciliation_passed,
      sop_satisfied: latestEmail.sop_satisfied,
      reason: latestEmail.body_preview,
    } : null,
    actions_completed:  row.agent_actions || null,
    exception_details: {
      exception_id:   row.exception_id,
      exception_type: row.exception_name,
      invoice_number: row.document_number,
    },
  };
}

// ─── Exported helper — trigger Agent 1 ───────────────────────────────────────
export async function triggerP2PAgent1(exceptionId) {
  const result = await triggerAndPoll(`resolve this exception ${exceptionId}`);
  // Try to refetch from DB for fresh state
  try {
    const row = await fetchCaseFromDB(exceptionId);
    return mapDbToAgent1State(row);
  } catch {
    // Fallback: use webhook result directly
    return result;
  }
}

// ─── Strip em dashes from DB text ─────────────────────────────────────────────
function clean(text) {
  if (!text) return text;
  return String(text).replace(/\s*—\s*/g, " ").replace(/—/g, " ");
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ size = "md" }) {
  const sz = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-8 h-8" : "w-5 h-5";
  return (
    <svg className={`${sz} animate-spin text-[#0070F2]`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
    </svg>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const upper = (value || "").toUpperCase().replace(/ /g, "_");
  const map = {
    RESOLVED:         "bg-emerald-50 text-emerald-700 border-emerald-200",
    IN_PROGRESS:      "bg-blue-50 text-blue-700 border-blue-200",
    ESCALATED:        "bg-red-50 text-red-700 border-red-200",
    OPEN:             "bg-amber-50 text-amber-700 border-amber-200",
    REJECTED:         "bg-red-100 text-red-800 border-red-300",
    ON_HOLD:          "bg-slate-50 text-slate-700 border-slate-300",
  };
  const dotMap = {
    RESOLVED: "bg-emerald-500", IN_PROGRESS: "bg-blue-500", ESCALATED: "bg-red-500",
    OPEN: "bg-amber-500", REJECTED: "bg-red-700", ON_HOLD: "bg-slate-400",
  };
  const cls = map[upper] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotMap[upper] || "bg-gray-400"}`} />
      {upper.replace(/_/g, " ")}
    </span>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ accent = "blue", title, children }) {
  const headers = {
    blue:   "bg-[#003399]",
    green:  "bg-gradient-to-r from-emerald-600 to-emerald-500",
    teal:   "bg-gradient-to-r from-teal-700 to-teal-500",
    purple: "bg-gradient-to-r from-violet-700 to-violet-500",
    orange: "bg-gradient-to-r from-orange-600 to-orange-400",
    slate:  "bg-gradient-to-r from-slate-700 to-slate-500",
    red:    "bg-gradient-to-r from-red-700 to-red-500",
  };
  const borders = {
    blue:   "border-[#B8D6FF]", green:  "border-emerald-200", teal:   "border-teal-200",
    purple: "border-violet-200", orange: "border-orange-200", slate:  "border-slate-200",
    red:    "border-red-200",
  };
  return (
    <div className={`rounded-2xl border ${borders[accent] || borders.blue} shadow-sm overflow-hidden bg-white`}>
      <div className={`${headers[accent] || headers.blue} px-5 py-3 flex items-center gap-2.5`}>
        <h3 className="text-sm font-bold uppercase tracking-widest text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── KV pair ──────────────────────────────────────────────────────────────────
function KV({ label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-0.5">{label}</p>
      <p className={`text-sm font-semibold text-[#1D2D3E] ${mono ? "font-mono" : ""}`}>{clean(value)}</p>
    </div>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
function Timeline({ steps, accent = "blue" }) {
  if (!steps?.length) return (
    <p className="text-sm text-[#556B82] italic px-5 py-4">No actions recorded yet.</p>
  );
  const dotBg = accent === "teal" ? "bg-teal-500 ring-teal-100" : "bg-[#0070F2] ring-[#B8D6FF]";
  return (
    <div className="px-5 py-5">
      {steps.map((s, idx) => {
        const desc = typeof s === "string" ? s : s.description || s.agent_action || JSON.stringify(s);
        const isPending = typeof s === "object" && (s.status || "").toLowerCase() === "pending";
        const isLast    = idx === steps.length - 1;
        const stepNum   = idx + 1; // Always use index-based numbering
        return (
          <div key={idx} className="flex gap-4 items-stretch">
            <div className="flex flex-col items-center flex-shrink-0 w-8">
              <div className="w-0.5 bg-[#B8D6FF] flex-1" />
              <div className={`w-8 h-8 rounded-full ${isPending ? "bg-amber-400 ring-amber-100" : dotBg} ring-4 flex items-center justify-center shadow-sm flex-shrink-0`}>
                <span className="text-white text-xs font-bold">{stepNum}</span>
              </div>
              {isLast ? <div className="flex-1" /> : <div className="w-0.5 bg-[#B8D6FF] flex-1" />}
            </div>
            <div className="flex-1 py-2 pb-5">
              <div className="bg-[#EBF4FF] border border-[#B8D6FF] rounded-xl p-4 shadow-sm">
                <p className="text-sm text-[#1D2D3E] leading-relaxed">{clean(desc)}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Exception info ───────────────────────────────────────────────────────────
function ExceptionInfo({ info, rowData }) {
  const d = info    || {};
  const r = rowData || {};
  return (
    <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
      <KV label="Invoice #"       value={d.invoice_number  || r.document_number} mono />
      <KV label="Vendor"          value={d.customer_name   || r.vendor_name} />
      <KV label="Exception Type"  value={d.exception_type  || r.exception_name} />
      <KV label="Invoice Amount"  value={d.invoice_amount  || (r.amount ? `USD ${r.amount}` : null)} mono />
      <KV label="SOP Reference"   value={d.sop_reference   || r.sop_reference} />
      <KV label="Overdue"         value={d.overdue === true || d.overdue === "true" ? "⚠ Yes" : "No"} />
      {(d.status || r.status) && (
        <div className="col-span-2 pt-2 border-t border-[#EBF4FF]">
          <StatusBadge value={d.status || r.status} />
        </div>
      )}
    </div>
  );
}

// ─── Email card ───────────────────────────────────────────────────────────────
function EmailCard({ email }) {
  if (!email) return (
    <p className="text-sm text-[#556B82] italic px-5 py-4">No notification data available.</p>
  );
  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-base font-bold text-[#556B82] uppercase tracking-widest">To:</span>
        <span className="text-sm font-semibold text-[#1D2D3E]">{clean(email.sent_to || email.to)}</span>
      </div>
      {email.subject && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-0.5">Subject</p>
          <p className="text-sm font-semibold text-[#1D2D3E]">{clean(email.subject)}</p>
        </div>
      )}
      {(email.body_preview || email.action || email.outcome) && (
        <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 space-y-2.5">
          {email.body_preview && (
            <p className="text-sm text-[#1D2D3E] leading-relaxed font-medium">{clean(email.body_preview)}</p>
          )}
          {email.action && (
            <div className="pt-2 border-t border-[#B8D6FF]">
              <p className="text-xs font-bold text-[#556B82] uppercase tracking-wider mb-1">Action Taken</p>
              <p className="text-sm text-[#0040B0] font-semibold">{email.action}</p>
            </div>
          )}
          {email.outcome && (
            <div className="pt-2 border-t border-[#B8D6FF]">
              <p className="text-xs font-bold text-[#556B82] uppercase tracking-wider mb-1">Outcome</p>
              <p className="text-sm text-[#0040B0] font-semibold">{email.outcome}</p>
            </div>
          )}
        </div>
      )}
      {email.sent_at && (
        <p className="text-xs text-[#556B82]">Sent: {new Date(email.sent_at).toLocaleString()}</p>
      )}
    </div>
  );
}

// ─── Next steps ───────────────────────────────────────────────────────────────
function NextSteps({ data }) {
  if (!data) return null;
  const { next_steps = [], deadlines = [], escalation_triggers = [] } = data;
  if (!next_steps.length && !deadlines.length && !escalation_triggers.length) return null;
  return (
    <div className="p-5 space-y-4">
      {next_steps.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Next Steps</p>
          <ul className="space-y-1.5">{next_steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
              <span className="mt-0.5">{clean(s)}</span>
            </li>
          ))}</ul>
        </div>
      )}
      {deadlines.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Deadlines</p>
          <ul className="space-y-1.5">{deadlines.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
              {clean(d)}
            </li>
          ))}</ul>
        </div>
      )}
      {escalation_triggers.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Escalation Triggers</p>
          <ul className="space-y-1.5">{escalation_triggers.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
              {clean(t)}
            </li>
          ))}</ul>
        </div>
      )}
    </div>
  );
}

// ─── Agent 2 preview card ─────────────────────────────────────────────────────
function Agent2PreviewCard({ data }) {
  const outcome = data?.resolution_outcome    || {};
  const excDet  = data?.exception_details     || {};
  const reply   = data?.reply_analysis        || {};
  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center gap-3 flex-wrap bg-[#EBF4FF] rounded-xl px-4 py-3 border border-[#B8D6FF]">
        <StatusBadge value={outcome.status || "PENDING_APPROVAL"} />
        {outcome.reason && (
          <span className="text-sm text-[#1D2D3E] flex-1 font-medium">{clean(outcome.reason)}</span>
        )}
      </div>

      {(excDet.exception_id || excDet.exception_type) && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Exception</p>
          <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-sm space-y-2">
            {excDet.exception_id   && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">ID: </span>{excDet.exception_id}</p>}
            {excDet.exception_type && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Type: </span>{clean(excDet.exception_type)}</p>}
            {excDet.invoice_number && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Invoice: </span>{excDet.invoice_number}</p>}
          </div>
        </div>
      )}

      {reply.summary && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Reply Analysis</p>
          <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-sm space-y-2">
            <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Summary: </span>{clean(reply.summary)}</p>
            <p className="text-[#1D2D3E]">
              <span className="font-bold text-[#0040B0]">Satisfies SOP: </span>
              {reply.satisfies_sop_criteria === true || reply.satisfies_sop_criteria === "true" ? "Yes" : "No"}
            </p>
            {reply.reason && (
              <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Reason: </span>{clean(reply.reason)}</p>
            )}
          </div>
        </div>
      )}

      {Array.isArray(data?.actions_completed) && data.actions_completed.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Steps Completed</p>
          <Timeline steps={data.actions_completed} accent="teal" />
        </div>
      )}
    </div>
  );
}

// ─── Resolution Details Card (HITL Edits) ────────────────────────────────────
function ResolutionDetailsCard({ resolutionDetails }) {
  if (!resolutionDetails) return null;
  
  const { resolution, field_edits, edits_count } = resolutionDetails;
  
  return (
    <div className="p-5 space-y-5">
      {/* Resolution Summary */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">✏️</span>
          <div className="flex-1">
            <p className="text-base font-bold text-violet-900">Human-Edited Resolution</p>
            <p className="text-sm text-violet-700">
              Approved by {resolution.approver_email || resolution.resolved_by} with {edits_count} field edit{edits_count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        
        {resolution.approver_comments && (
          <div className="pt-2 border-t border-violet-200">
            <p className="text-xs font-bold text-violet-800 uppercase tracking-wider mb-1">Comments</p>
            <p className="text-sm text-violet-900 italic">"{resolution.approver_comments}"</p>
          </div>
        )}
        
        {resolution.edit_flags && resolution.edit_flags.length > 0 && (
          <div className="pt-2 border-t border-violet-200">
            <p className="text-xs font-bold text-violet-800 uppercase tracking-wider mb-2">Flags</p>
            <div className="flex flex-wrap gap-2">
              {resolution.edit_flags.map((flag, idx) => (
                <span key={idx} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 border border-violet-300">
                  🚩 {flag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Field Edits */}
      {field_edits && field_edits.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-3">Field Changes</p>
          <div className="space-y-3">
            {field_edits.map((edit) => {
              const fieldLabel = edit.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const originalVal = typeof edit.original_value === 'object' ? JSON.stringify(edit.original_value) : edit.original_value;
              const editedVal = typeof edit.edited_value === 'object' ? JSON.stringify(edit.edited_value) : edit.edited_value;
              const isWarning = edit.validation_status === 'warning';
              
              return (
                <div key={edit.edit_id} className={`border rounded-xl p-4 ${isWarning ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <p className="text-sm font-bold text-[#1D2D3E]">{fieldLabel}</p>
                    {isWarning && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">
                        ⚠ Warning
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-bold text-[#556B82] uppercase tracking-wider mb-1">Original</p>
                      <p className="text-[#1D2D3E] font-mono bg-white rounded px-2 py-1 border border-gray-200">
                        {originalVal}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#556B82] uppercase tracking-wider mb-1">Changed To</p>
                      <p className="text-[#0040B0] font-mono font-bold bg-white rounded px-2 py-1 border border-blue-300">
                        {editedVal}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-[#556B82]">
                      Edited by {edit.edited_by} on {new Date(edit.edited_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="text-xs text-[#556B82] bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
        <span className="font-bold">Resolution Method:</span> {resolution.resolution_method?.replace(/_/g, ' ')}
        {resolution.resolved_at && ` • Resolved at ${new Date(resolution.resolved_at).toLocaleString()}`}
      </div>
    </div>
  );
}

// ─── Loading / Error ──────────────────────────────────────────────────────────
function LoadingScreen({ message }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[320px] gap-4 p-8">
      <Spin size="lg" />
      <p className="text-base font-semibold text-[#1D2D3E] text-center">{message}</p>
      <p className="text-sm text-[#556B82] text-center">This may take up to 90 seconds</p>
      <div className="flex gap-1.5 mt-2">
        {[0,1,2].map((i) => (
          <div key={i} className="w-2 h-2 rounded-full bg-[#0070F2] animate-bounce"
               style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[240px] gap-4 p-8">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <span className="text-red-600 text-xl font-bold">X</span>
      </div>
      <p className="text-base font-semibold text-[#1D2D3E] text-center">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}
          className="px-5 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] transition-all shadow-sm">
          Retry
        </button>
      )}
    </div>
  );
}

// ─── P2PResolveSidebar ────────────────────────────────────────────────────────
export default function P2PResolveSidebar({
  rowData, isOpen, onClose, onStatusChange,
  phase, setPhase, agent1, setAgent1, agent2, setAgent2,
  activeCaseId,
}) {
  const row         = rowData || {};
  const exceptionId = activeCaseId || row.exception_id || "";

  const [errorMsg, setErrorMsg] = useState("");
  const [editableFieldsData, setEditableFieldsData] = useState(null);
  const [resolutionDetails, setResolutionDetails] = useState(null);
  // Bumped after any DB-changing action so the trail panel re-fetches from the DB.
  const [trailRefresh, setTrailRefresh] = useState(0);
  const refreshTrail = () => setTrailRefresh((n) => n + 1);
  const pollingRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const isUserActionRef = useRef(false); // Track if user is actively editing/approving

  // ── Fetch agent data when sidebar opens ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || !exceptionId) {
      // Clear polling when sidebar closes
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Fetch immediately on open
    const fetchData = async () => {
      // Don't update phase if user is actively editing or approving
      if (isUserActionRef.current) {
        return; // Skip polling updates during user actions
      }
      
      try {
        const freshRow = await fetchCaseFromDB(exceptionId);
        const mapped = mapDbToAgent1State(freshRow);
        
        // Always set agent1 data if we have actions or email notification
        // This ensures timeline loads for cases processed outside the portal
        if (mapped.actions_performed?.length > 0 || mapped.email_notification) {
          setAgent1(mapped);
          
          // Determine phase based on DB status and latest communication
          const dbStatus = freshRow.status?.toUpperCase();
          const latestComm = freshRow.email_content?.[0];
          
          // Only auto-advance if user hasn't made a decision yet
          if (dbStatus === 'RESOLVED') {
            // Exception is resolved - show RESOLVED state with resolution details
            const agent2Data = mapDbToAgent2State(freshRow);
            setAgent2(agent2Data);
            setPhase(S.AGENT2_RESOLVED);
            
            // Fetch resolution details to show HITL edits if any
            try {
              const resDetailsRes = await fetch(`${P2P_URL}/${exceptionId}/resolution`);
              if (resDetailsRes.ok) {
                const resDetails = await resDetailsRes.json();
                setResolutionDetails(resDetails);
              }
            } catch (err) {
              console.warn('Could not fetch resolution details:', err);
            }
          } else if (dbStatus === 'ESCALATED' || latestComm?.outcome === 'ESCALATED') {
            // ESCALATED status - either from DB status or from agent outcome
            // This handles reconciliation failures, wrong doc type, or SOP not satisfied
            const agent2Data = mapDbToAgent2State(freshRow);
            setAgent2(agent2Data);
            setPhase(S.AGENT2_REJECTED);
          } else if (dbStatus === 'IN_PROGRESS' && latestComm?.outcome === 'IN_PROGRESS' && latestComm?.reconciliation_passed && latestComm?.sop_satisfied) {
            // Document validated - show PREVIEW with approve/reject/edit options
            const agent2Data = mapDbToAgent2State(freshRow);
            setAgent2(agent2Data);
            setPhase(S.AGENT2_PREVIEW);
          } else if (phase === S.IDLE) {
            setPhase(S.AGENT1_DONE);
          }
        }
      } catch (err) {
        console.warn('Could not fetch agent data:', err);
      }
    };

    fetchData();

    // Set up polling every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchData();
    }, 3000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, exceptionId]);

  // ── Agent 2 — check for reply (PREVIEW) ──────────────────────────────────
  const runAgent2Scan = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase(S.AGENT2_LOADING);
    setErrorMsg("");
    try {
      const result = await triggerAndPoll(`check responses for exception ${exceptionId}`);
      const mapped = result?.resolution_outcome ? result : mapDbToAgent2State(await fetchCaseFromDB(exceptionId).catch(() => ({})));
      setAgent2(mapped);
      refreshTrail(); // reply handler wrote to the DB — re-fetch the trail so it shows live

      // Determine phase based on outcome
      const mode = (mapped.mode || "").toLowerCase();
      const outcome = (mapped.resolution_outcome?.status || "").toUpperCase();
      
      if (mode === "no_reply") {
        setPhase(S.AGENT2_NO_REPLY);
      } else if (outcome === "ESCALATED" || outcome === "REJECTED") {
        setPhase(S.AGENT2_REJECTED);
        if (onStatusChange) onStatusChange(exceptionId, "ESCALATED");
      } else if (outcome === "IN_PROGRESS") {
        // Show PREVIEW so user can approve/reject/edit
        setPhase(S.AGENT2_PREVIEW);
      } else {
        setPhase(S.AGENT2_PREVIEW);
      }
    } catch (err) {
      setErrorMsg(err.message || "Agent 2 failed");
      setPhase(S.AGENT2_ERROR);
    } finally { pollingRef.current = false; }
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    isUserActionRef.current = true; // Block polling during approval
    setPhase(S.AGENT2_APPROVING);
    setErrorMsg("");
    try {
      // Direct DB write - no agent call
      const res = await fetch(`${P2P_URL}/${exceptionId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'RESOLVED',
          notes: 'Approved by human reviewer without field edits'
        }),
      });
      
      if (!res.ok) throw new Error(`Approval failed: ${res.status}`);
      
      // Refetch to get updated state
      const freshRow = await fetchCaseFromDB(exceptionId);
      const agent2Data = mapDbToAgent2State(freshRow);
      setAgent2(agent2Data);
      setPhase(S.AGENT2_RESOLVED);
      if (onStatusChange) onStatusChange(exceptionId, "RESOLVED");
      refreshTrail();
    } catch (err) {
      setErrorMsg(err.message || "Approval failed");
      setPhase(S.AGENT2_ERROR);
    } finally { 
      pollingRef.current = false;
      isUserActionRef.current = false; // Resume polling after action completes
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    isUserActionRef.current = true; // Block polling during rejection
    setPhase(S.AGENT2_APPROVING);
    setErrorMsg("");
    try {
      // Direct DB write - no agent call
      const res = await fetch(`${P2P_URL}/${exceptionId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'ESCALATED',
          notes: 'Rejected by human reviewer - resolution not accepted'
        }),
      });
      
      if (!res.ok) throw new Error(`Rejection failed: ${res.status}`);
      
      // Refetch to get updated state
      const freshRow = await fetchCaseFromDB(exceptionId);
      const agent2Data = mapDbToAgent2State(freshRow);
      setAgent2(agent2Data);
      setPhase(S.AGENT2_REJECTED);
      if (onStatusChange) onStatusChange(exceptionId, "ESCALATED");
      refreshTrail();
    } catch (err) {
      setErrorMsg(err.message || "Rejection failed");
      setPhase(S.AGENT2_REJECTED);
      if (onStatusChange) onStatusChange(exceptionId, "ESCALATED");
    } finally { 
      pollingRef.current = false;
      isUserActionRef.current = false; // Resume polling after action completes
    }
  };

  // ── Edit Fields (HITL) ────────────────────────────────────────────────────
  const handleEditFields = async () => {
    setErrorMsg("");
    isUserActionRef.current = true; // Block polling while editing
    try {
      const res = await fetch(`${P2P_URL}/${exceptionId}/editable-fields`);
      if (!res.ok) {
        if (res.status === 404) {
          setErrorMsg("Field editing not available. The agent did not extract editable fields from the document. This feature will be available once the agent is updated.");
          isUserActionRef.current = false; // Resume polling on error
          return;
        }
        throw new Error(`Failed to fetch editable fields: ${res.status}`);
      }
      const data = await res.json();
      setEditableFieldsData(data);
      setPhase(S.AGENT2_EDITING);
    } catch (err) {
      setErrorMsg(err.message || "Failed to load editable fields");
      isUserActionRef.current = false; // Resume polling on error
    }
  };

  // ── Submit HITL Edits ─────────────────────────────────────────────────────
  const handleSubmitEdits = async (payload) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase(S.AGENT2_APPROVING);
    setErrorMsg("");
    try {
      const res = await fetch(`${P2P_URL}/${exceptionId}/approve-with-edits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Approval failed: ${res.status}`);
      const result = await res.json();
      
      // Fetch resolution details including field edits
      const resDetailsRes = await fetch(`${P2P_URL}/${exceptionId}/resolution`);
      if (resDetailsRes.ok) {
        const resDetails = await resDetailsRes.json();
        setResolutionDetails(resDetails);
      }
      
      // Refetch to get updated state with human_edited flag
      const freshRow = await fetchCaseFromDB(exceptionId);
      const agent2Data = mapDbToAgent2State(freshRow);
      agent2Data.human_edited = true; // Mark as edited
      setAgent2(agent2Data);
      setPhase(S.AGENT2_RESOLVED);
      if (onStatusChange) onStatusChange(exceptionId, "RESOLVED");
      refreshTrail();
    } catch (err) {
      setErrorMsg(err.message || "Approval with edits failed");
      setPhase(S.AGENT2_ERROR);
    } finally { 
      pollingRef.current = false;
      isUserActionRef.current = false; // Resume polling after submit
    }
  };

  // ── Cancel Editing ────────────────────────────────────────────────────────
  const handleCancelEditing = () => {
    setEditableFieldsData(null);
    setPhase(S.AGENT2_PREVIEW);
    isUserActionRef.current = false; // Resume polling when canceling edit
  };

  const invoiceDisplay = agent1?.exception_information?.invoice_number || row.document_number || "N/A";

  return (
    <div className="fixed inset-0 z-50 flex pointer-events-none">
      <div className="flex-1" />
      <div className={`w-full max-w-3xl bg-[#F4F8FF] shadow-2xl flex flex-col h-full overflow-hidden border-l border-[#B8D6FF] pointer-events-auto transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header — dark gradient, invoice chip, vendor, amount · PO · exception */}
        <div className="flex-shrink-0 px-6 pt-4 pb-4 flex items-start justify-between gap-3"
             style={{ background: "linear-gradient(90deg,#1B2735,#354A5F)" }}>
          <div className="flex-1 min-w-0">
            {row?.document_number && (
              <span className="inline-block mb-2 font-mono text-[11px] tracking-wide px-1.5 py-0.5 rounded"
                    style={{ color: "rgba(255,255,255,.85)", background: "rgba(255,255,255,.10)", border: "1px solid rgba(255,255,255,.18)" }}>
                {row.document_number}
              </span>
            )}
            <h2 className="text-xl font-semibold leading-tight tracking-tight text-white truncate">
              {row?.vendor_name || "P2P Exception Resolution"}
            </h2>
            {(row?.amount != null || row?.po_number || row?.exception_name) && (
              <div className="text-[13px] mt-0.5" style={{ color: "rgba(255,255,255,.72)" }}>
                {[
                  row?.amount != null ? `${row?.currency || "USD"} ${Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null,
                  row?.po_number || null,
                  row?.exception_name ? String(row.exception_name).replace(/_/g, " ").toLowerCase() : null,
                ].filter(Boolean).join("  ·  ")}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/12 transition-colors flex-shrink-0" aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {!exceptionId && (
            <div className="flex flex-col items-center justify-center min-h-[320px] gap-4 p-8 text-center">
              <p className="text-base font-semibold text-[#1D2D3E]">No exception selected</p>
              <p className="text-sm text-[#556B82]">Click the panel icon on any row to view details,<br/>or click Resolve to trigger Agent 1.</p>
            </div>
          )}

          {exceptionId && phase === S.IDLE && (
            /* Any opened exception shows the DB-driven trail (Summary + 4 tabs),
               not the old cached Exception Details card. */
            <P2PTrailPanel exceptionId={exceptionId} isOpen={isOpen} rowData={row} refreshSignal={trailRefresh} />
          )}

          {phase === S.AGENT1_LOADING && (
            <LoadingScreen message="Agent 1 is analyzing the exception and preparing notifications…" />
          )}

          {phase === S.AGENT1_ERROR && (
            <ErrorScreen message={errorMsg || "Agent 1 failed to complete."} />
          )}

          {[S.AGENT1_DONE, S.AGENT2_LOADING, S.AGENT2_NO_REPLY, S.AGENT2_PREVIEW, S.AGENT2_EDITING,
            S.AGENT2_APPROVING, S.AGENT2_RESOLVED, S.AGENT2_REJECTED, S.AGENT2_ERROR
          ].includes(phase) && (
            <>
              {/* Redesigned agent action trail — Summary + run header + 4 tabs,
                  driven live from GET /api/p2p/exceptions/:id/trail (replaces the
                  old cached Exception Details + Agent Actions Timeline cards). */}
              <P2PTrailPanel exceptionId={exceptionId} isOpen={isOpen} rowData={row} refreshSignal={trailRefresh} />

              {agent1?.next_steps_and_deadlines && (
                <SectionCard accent="orange" title="Next Steps & Deadlines">
                  <NextSteps data={agent1.next_steps_and_deadlines} />
                </SectionCard>
              )}
            </>
          )}

          {phase === S.AGENT2_LOADING && (
            <LoadingScreen message="Agent 2 is scanning for replies…" />
          )}

          {phase === S.AGENT2_APPROVING && (
            <LoadingScreen message="Processing your decision and updating the database…" />
          )}

          {phase === S.AGENT2_ERROR && (
            <ErrorScreen message={errorMsg || "Agent 2 failed."} onRetry={runAgent2Scan} />
          )}

          {phase === S.AGENT2_NO_REPLY && (
            <SectionCard accent="slate" title="No Reply Yet">
              <div className="p-5">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-base font-semibold text-[#1D2D3E] mb-1">No reply received yet</p>
                  <p className="text-sm text-[#556B82]">
                    {clean(agent2?.resolution_outcome?.reason || "No reply found for this exception. The response deadline has not yet passed.")}
                  </p>
                </div>
              </div>
            </SectionCard>
          )}

          {phase === S.AGENT2_PREVIEW && agent2 && (
            <>
              {/* The reply itself is rendered by the DB-driven trail panel above
                  (Communications tab). Here we only prompt for the human decision;
                  the Approve / Reject / Edit actions live in the footer. */}
              <SectionCard accent="purple" title="Reply Received — Your Decision">
                <div className="p-5">
                  <p className="text-sm text-[#556B82]">
                    A reply was received and recorded. Review it in the trail above
                    (Communications tab), then Approve, Edit fields, or Reject below.
                  </p>
                </div>
              </SectionCard>
              {errorMsg && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  <p className="font-semibold mb-1">⚠️ Note</p>
                  <p>{errorMsg}</p>
                </div>
              )}
            </>
          )}

          {phase === S.AGENT2_EDITING && editableFieldsData && (
            <SectionCard accent="violet" title="Human-in-the-Loop — Field Editing">
              <EditableFieldsForm
                exceptionId={exceptionId}
                exceptionCode={editableFieldsData.exception_code}
                extractedFields={editableFieldsData.extracted_fields}
                fieldEditRules={editableFieldsData.field_edit_rules}
                onSubmit={handleSubmitEdits}
                onCancel={handleCancelEditing}
                isSubmitting={false}
              />
            </SectionCard>
          )}

          {/* Resolved / Rejected states are shown by the DB-driven trail panel above
              (which runs every time) — no separate cached "Agent 2" card is rendered. */}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#B8D6FF] bg-white flex items-center justify-between gap-3 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#556B82] border border-[#B8D6FF] rounded-lg hover:bg-[#EBF4FF] transition-colors">
            Close
          </button>
          <div className="flex items-center gap-2">
            {/* Only show "Check for Reply" if action is not AUTO_POST */}
            {(phase === S.AGENT1_DONE || phase === S.AGENT2_NO_REPLY) && 
             agent1?.email_notification?.action !== 'AUTO_POST' && (
              <button type="button" onClick={runAgent2Scan}
                className="px-5 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] transition-all shadow-sm flex items-center gap-2">
                <span>{phase === S.AGENT2_NO_REPLY ? "Check Again" : "Check for Reply"}</span>
              </button>
            )}
            {phase === S.AGENT2_PREVIEW && (
              <>
                <button type="button" onClick={handleReject}
                  className="px-4 py-2 text-sm font-bold text-red-700 border border-red-300 bg-red-50 rounded-lg hover:bg-red-100 transition-all">
                  Reject
                </button>
                {/* Show Edit Fields button when in PREVIEW phase
                    - Agent 2 has completed validation
                    - outcome is IN_PROGRESS (document validated, not escalated)
                    - ALWAYS show for HITL editing (all 9 exception types)
                */}
                {agent2?.resolution_outcome?.status === 'IN_PROGRESS' && (
                  <button type="button" onClick={handleEditFields}
                    className="px-4 py-2 text-sm font-bold text-violet-700 border border-violet-300 bg-violet-50 rounded-lg hover:bg-violet-100 transition-all">
                    Edit Fields
                  </button>
                )}
                {/* Always show Approve button in PREVIEW phase */}
                <button type="button" onClick={handleApprove}
                  className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-all shadow-sm flex items-center gap-2">
                  Approve
                </button>
              </>
            )}
            {phase === S.AGENT2_RESOLVED && <StatusBadge value="RESOLVED" />}
            {phase === S.AGENT2_RESOLVED && agent2?.human_edited && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border bg-violet-50 text-violet-700 border-violet-200">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                ✏️ HUMAN EDITED
              </span>
            )}
            {phase === S.AGENT2_REJECTED && (
              <>
                <StatusBadge value="ESCALATED" />
                <button type="button" onClick={runAgent2Scan}
                  className="px-5 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] transition-all shadow-sm flex items-center gap-2">
                  <span>Check for Reply</span>
                </button>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
